import { useNavigate } from 'react-router';

import {
  clearStorage,
  fullLogout,
  useGetLoggedInUser,
  useIsRealRevelioAdmin,
} from '@revelio/auth';
import { Role } from '@revelio/data-access';
import {
  Badge,
  BadgeColor,
  Menu as UiMenu,
  MenuButton as UiMenuButton,
  MenuDivider as UiMenuDivider,
  MenuItem as UiMenuItem,
  MenuList as UiMenuList,
  MenuRadioGroup as UiMenuRadioGroup,
  MenuRadioItem as UiMenuRadioItem,
  MenuSub as UiMenuSub,
  MenuSubContent as UiMenuSubContent,
  MenuSubTrigger as UiMenuSubTrigger,
} from '@revelio/ui';

import { dashMetaFetch } from '../../../../shared/api/dash-meta-fetch';
import {
  isInspectorActive,
  useDataInspectorStore,
} from '../../../../shared/data-inspector/data-inspector.store';
import { trackDataInspectorUsed } from '../../../../shared/data-inspector/data-inspector-tracking';
import { useToast } from '../../../../shared/hooks/useToast';
import { useColorModePreference } from '../../../../shared/user-preferences/hooks/useColorModePreference';
import type { ColorModePreference } from '../../../../shared/user-preferences/types';
import navPanelStyles from '../expanded-nav/ExpandedNav.module.css';
import menuStyles from './menus.module.css';
import type { RailTutorials } from './tutorials-anchor';

interface AccountMenuProps {
  version?: string;
  onOpenImpersonationModal?: () => void;
  tutorials?: RailTutorials;
}

/** First letters of the first two words, e.g. "Testing Framework" -> "TF". */
const toInitials = (name: string): string =>
  name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((word) => word[0] ?? '')
    .join('')
    .toUpperCase();

type AccountMenuEntry =
  | { key: string; divider: true }
  | { key: string; appearance: true }
  | {
      key: string;
      label: string;
      onClick?: () => void;
      testId?: string;
      unwatched?: boolean;
    };

const APPEARANCE_OPTIONS: {
  value: ColorModePreference;
  label: string;
}[] = [
  { value: 'system', label: 'System default' },
  { value: 'light', label: 'Light' },
  { value: 'dark', label: 'Dark' },
];

/** Appearance (light/dark/system) as a submenu off the account menu — the only
 *  home for the ColorMode preference now that it's out of the preferences
 *  modal. The trigger shows the active mode; selecting an option applies it. */
function AppearanceSubmenu({ themed }: { themed: boolean }) {
  const { mode, setColorMode } = useColorModePreference();
  const current = APPEARANCE_OPTIONS.find((option) => option.value === mode);

  return (
    <UiMenuSub>
      <UiMenuSubTrigger trailing={current?.label}>Appearance</UiMenuSubTrigger>
      <UiMenuSubContent
        size="sm"
        className={themed ? menuStyles.railSubMenu : undefined}
      >
        <UiMenuRadioGroup
          value={mode}
          onValueChange={(value) => setColorMode(value as ColorModePreference)}
        >
          {APPEARANCE_OPTIONS.map((option) => (
            <UiMenuRadioItem key={option.value} value={option.value}>
              {option.label}
            </UiMenuRadioItem>
          ))}
        </UiMenuRadioGroup>
      </UiMenuSubContent>
    </UiMenuSub>
  );
}

export function AccountMenu({
  version,
  onOpenImpersonationModal,
  tutorials,
}: AccountMenuProps) {
  const navigate = useNavigate();
  const toast = useToast();
  const isDataInspectorEnabled = useDataInspectorStore(isInspectorActive);
  const setDataInspectorEnabled = useDataInspectorStore(
    (state) => state.setEnabled
  );

  const { loggedInUser } = useGetLoggedInUser();
  const isRevelioAdmin = useIsRealRevelioAdmin();
  const userId = loggedInUser.id;
  const username = loggedInUser.name;
  const email = loggedInUser.email;
  const displayName = username || email || 'Account';
  const initials = toInitials(displayName);

  const handleClearCache = async () => {
    const clearSiteData = dashMetaFetch(
      '/api/auth/clear-site-data?types="cache", "storage"'
    );

    const manualClear = clearStorage();

    const clearAll = Promise.all([clearSiteData, manualClear]);

    toast.promise(clearAll, {
      loading: {
        title: 'Clearing Cache',
        description: 'Please wait a moment...',
        position: 'top-right',
      },
      success: {
        title: 'Cache Cleared',
        position: 'top-right',
        duration: 1000,
        onCloseComplete: () => window.location.reload(),
      },
      error: (error) => ({
        title: 'Error Clearing Cache',
        description: 'Please try again.',
        position: 'top-right',
        onCloseComplete: () => console.error(error),
      }),
    });
  };

  const entries: AccountMenuEntry[] = [
    {
      key: 'account',
      label: 'Account',
      onClick: () => navigate(`/manage/user/${userId}/edit`),
    },
    ...(tutorials
      ? [
          {
            key: 'tutorials',
            label: 'Tutorials',
            onClick: tutorials.openTutorials,
            testId: 'tutorials-button',
            unwatched: tutorials.hasUnwatchedVideo,
          },
        ]
      : []),
    { key: 'appearance', appearance: true as const },
    ...(isRevelioAdmin
      ? [
          {
            key: 'admin',
            label: 'Admin',
            onClick: () => navigate('/admin'),
          },
          {
            key: 'impersonation',
            label: 'Impersonation Settings',
            onClick: onOpenImpersonationModal,
            testId: 'impersonation-settings-menu-item',
          },
          {
            key: 'data-inspector',
            label: isDataInspectorEnabled
              ? 'Data Inspector: on'
              : 'Data Inspector',
            onClick: () => {
              trackDataInspectorUsed(
                isDataInspectorEnabled ? 'disabled' : 'enabled'
              );
              setDataInspectorEnabled(!isDataInspectorEnabled);
            },
            testId: 'data-inspector-menu-item',
          },
        ]
      : []),
    { key: 'divider-main', divider: true },
    ...(isRevelioAdmin
      ? [
          {
            key: 'clear-cache',
            label: 'Clear Cache',
            onClick: handleClearCache,
          },
        ]
      : []),
    ...(loggedInUser.role === Role.ClientAdmin
      ? [
          {
            key: 'user-management',
            label: 'User Management',
            onClick: () => navigate('/manage'),
          },
        ]
      : []),
    {
      key: 'logout',
      label: 'Logout',
      onClick: () => fullLogout(true),
    },
  ];

  return (
    <UiMenu>
      <UiMenuButton>
        <button
          type="button"
          aria-label="Account menu"
          data-testid="user-controls"
          data-rail-menu
          className={`${navPanelStyles.header} ${navPanelStyles.hoverExempt}`}
        >
          <span className={navPanelStyles.iconBox}>
            <span className={menuStyles.initials} aria-hidden="true">
              {initials}
            </span>
          </span>
          <span className={navPanelStyles.label}>{displayName}</span>
        </button>
      </UiMenuButton>
      <UiMenuList
        side="right"
        align="start"
        sideOffset={0}
        size="sm"
        className={menuStyles.railMenu}
      >
        <div className={menuStyles.identity}>
          <span className={menuStyles.initials} aria-hidden="true">
            {initials}
          </span>
          <span className={menuStyles.identityText}>
            <span className={menuStyles.identityName}>{displayName}</span>
            {email && username && (
              <span className={menuStyles.identityEmail}>{email}</span>
            )}
          </span>
        </div>
        <UiMenuDivider />
        {entries.map((entry) => {
          if ('divider' in entry) {
            return <UiMenuDivider key={entry.key} />;
          }
          if ('appearance' in entry) {
            return <AppearanceSubmenu key={entry.key} themed />;
          }
          return (
            <UiMenuItem
              key={entry.key}
              onClick={entry.onClick}
              data-testid={entry.testId}
            >
              {entry.unwatched ? (
                <span className={menuStyles.unwatchedItem}>
                  {entry.label}
                  <Badge
                    size="sm"
                    color={BadgeColor.PRIMARY}
                    className={menuStyles.unwatchedIndicator}
                    data-testid="tutorial-unwatched-indicator"
                    aria-hidden="true"
                  />
                </span>
              ) : (
                entry.label
              )}
            </UiMenuItem>
          );
        })}
        {version && (
          <div className={menuStyles.versionLine}>
            <span className={menuStyles.versionBadge}>{version}</span>
          </div>
        )}
      </UiMenuList>
    </UiMenu>
  );
}
